import * as autobind from 'autobind';
import * as React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'redux-scaffolding-ts';
import { Modal, Button, Form, Container, TextArea, Dimmer, Loader, Message, Icon } from 'semantic-ui-react';
import LanguageEditor from 'widgets/bussiness/language-editor';
import SkillsEditor, { SkillItemReference, SkillType } from 'widgets/bussiness/skills-editor';
import {
  ChangeIwsQuestionBankStore,
  ChangeIwsQuestionBankDto,
  IwsQuestionBankDto
} from 'stores/assessments/questionBank/iws-questionBank-store';
import { LanguagesStore } from 'stores/configuration/locations/languages-store';
import { nameof } from 'utils/object';
import { ItemReference } from 'stores/dataStore';
import i18n from 'i18n';
import { ChangeQuestionDto, QuestionDto } from 'stores/assessments/questionBank/tna-questionBank-store';
import LocationEditorWithRoleLocations from 'widgets/bussiness/location-editor-with-rolelocations';
import { resolve } from 'inversify-react';
import { IdentityService } from 'services/identity-service';

interface ChangeIwsQuestionBankViewProps extends WithTranslation {
  onClose: (isSuccess: boolean) => void;
  changeQuestionBank?: ChangeIwsQuestionBankStore;
  languagesStore?: LanguagesStore;
  currentData: IwsQuestionBankDto;
}

interface ChangeIwsQuestionBankState {
  skill: ItemReference;
  userClickedSave: boolean;
  questionTranslations: ChangeQuestionDto[];
}

@connect(['changeQuestionBank', ChangeIwsQuestionBankStore], ['languagesStore', LanguagesStore])
class ChangeIwsQuestionBankView extends React.Component<ChangeIwsQuestionBankViewProps, ChangeIwsQuestionBankState> {
  private get changeQuestionBankStore() {
    return this.props.changeQuestionBank;
  }
  @resolve(IdentityService)
  private identityService: IdentityService;

  constructor(props: ChangeIwsQuestionBankViewProps) {
    super(props);

    this.state = {
      skill: Object.assign(
        this.props.currentData.iwsSkill
          ? { id: this.props.currentData.iwsSkillId, title: this.props.currentData.iwsSkill.name }
          : { id: null, title: null }
      ),
      userClickedSave: false,
      questionTranslations: this.props.currentData.questionTranslations
        ? this.mapToCreateQuestionDto(this.props.currentData.questionTranslations)
        : []
    };
    this.changeQuestionBankStore.state.result = null;

    this.changeQuestionBankStore.change({
      id: this.props.currentData.id,
      question: { text: this.props.currentData.question.text, languageId: this.props.currentData.question.language.id },
      iwsSkillId: this.props.currentData.iwsSkillId,
      questionTranslations: this.props.currentData.questionTranslations
        ? this.mapToCreateQuestionDto(this.props.currentData.questionTranslations)
        : [],
      locationId: this.props.currentData.locationId,
      locationRequired: false
    });
  }

  componentDidMount() {
    const change = {};
    change['locationRequired'] = !!this.props.currentData.locationId;
    this.changeQuestionBankStore.change({ ...this.changeQuestionBankStore.state.item, ...change });
  }
  UNSAFE_componentWillReceiveProps(nextProps: ChangeIwsQuestionBankViewProps) {
    if (this.changeQuestionBankStore.state.result && this.changeQuestionBankStore.state.result.isSuccess) {
      nextProps.onClose(true);
    }
  }

  @autobind
  private handleValue(property: string, value: any) {
    this.setState({ userClickedSave: false });
    const change = {};
    change[property] = value;
    this.changeQuestionBankStore.change({ ...this.changeQuestionBankStore.state.item, ...change });
  }

  private mapToCreateQuestionDto(items: QuestionDto[]): ChangeQuestionDto[] {
    let data: ChangeQuestionDto[] = [];

    items.forEach(i => {
      data.push({ text: i.text, languageId: i.language.id });
    });
    return data;
  }

  private onChangeQuestion(q: string) {
    let question = this.changeQuestionBankStore.state.item.question;
    if (question) {
      question.text = q;
    } else {
      question = { text: q, languageId: this.props.currentData.question.language.id };
    }
    this.handleValue(nameof<ChangeIwsQuestionBankDto>('question'), question);
  }

  private onChangeSkill(s: SkillItemReference) {
    let id = s != null ? s.id : null;
    this.handleValue(nameof<ChangeIwsQuestionBankDto>('iwsSkillId'), id);
  }

  @autobind
  private onChangeItem() {
    this.changeQuestionBankStore.update();
  }

  @autobind
  private onCancelNewItem() {
    this.changeQuestionBankStore.clear();
    this.props.onClose(false);
  }

  addQuestionsTranslation = () => {
    let question = this.state.questionTranslations;
    question.push({ text: '', languageId: null });

    this.setState({ questionTranslations: question });
  };

  handleQuestionTransaltionTextChange = (index: number, questionText: string) => {
    let questionTranslations = this.state.questionTranslations;
    let question = questionTranslations[index];
    question.text = questionText;

    this.setState({ questionTranslations }, () =>
      this.handleValue(nameof<ChangeIwsQuestionBankDto>('questionTranslations'), questionTranslations)
    );
  };

  handleQuestionTransaltionLanguageChange = (index: number, languageId: string) => {
    let questionTranslations = this.state.questionTranslations;
    let question = questionTranslations[index];
    question.languageId = languageId;
    this.setState({ questionTranslations }, () =>
      this.handleValue(nameof<ChangeIwsQuestionBankDto>('questionTranslations'), questionTranslations)
    );
  };

  removeDateRange = index => {
    let questionTranslations = this.state.questionTranslations;
    questionTranslations.splice(index, 1);

    this.setState({ questionTranslations }, () =>
      this.handleValue(nameof<ChangeIwsQuestionBankDto>('questionTranslations'), questionTranslations)
    );
  };

  showQuestionTranslationRows = () => {
    return this.state.questionTranslations.map((question, index) => {
      return (
        <Form.Group className="tna-question-bank__new-rows" key={`${question.languageId}_${index}`}>
          <Form.Field
            required
            className="tna-question-bank__label-textarea"
            error={this.state.userClickedSave && question.text.length === 0}
          >
            <TextArea
              maxLength="500"
              className="tna-question-bank__new-rows__textarea"
              value={question ? question.text : ''}
              onChange={(e, { value }) => this.handleQuestionTransaltionTextChange(index, value.toString())}
            />
          </Form.Field>
          <div className="tna-question-bank__new-rows__drop-remove">
            <LanguageEditor
              className={'planit-user-dropdown-custom'}
              nullable
              value={question.languageId}
              onChange={c => {
                this.handleQuestionTransaltionLanguageChange(index, c == null ? null : c.id);
              }}
              onlyForTests={true}
            />

            <div className="new-pattern__remove-icon">
              <Icon className="clickable-icon remove-icon" color="red" name="remove" onClick={() => this.removeDateRange(index)} />
            </div>
          </div>
        </Form.Group>
      );
    });
  };

  public render() {
    const { t } = this.props as any;

    const modalTitle = 'Edit IWS Question';

    const currentUserInfo = this.identityService.getUserInfo();
    const areYouPoc = IdentityService.isPoc(currentUserInfo);
    const isAdminOrPwerInst = IdentityService.isAdminOrPowerInstructor(currentUserInfo);
    const pocLocations = currentUserInfo.locationsByRoles['PoC'] as string[];
    return (
      <Modal className="tna-question-bank__modal" open closeOnEscape={true} onClose={this.onCancelNewItem} closeOnDimmerClick={false}>
        <Dimmer active={this.changeQuestionBankStore.state.isBusy} style={{ background: 'rgba(0, 0, 0, 0.4)' }}>
          <Loader indeterminate>{t('')}</Loader>
        </Dimmer>
        <Modal.Header className="tna-squestion-bank__modal-header">{t(modalTitle)}</Modal.Header>
        <Modal.Content image>
          <Container>
            {this.changeQuestionBankStore.state.result && !this.changeQuestionBankStore.state.result.isSuccess && (
              <Message
                className="error-message__style"
                icon="exclamation circle"
                error
                header={t('An error ocurred')}
                list={this.changeQuestionBankStore.state.result.messages.map(o => o.body)}
              />
            )}
            {this.changeQuestionBankStore.state.item && (
              <Form>
                <div className="tna-question-bank__wrapper scroll">
                  <Form.Group className="tna-question-bank__selctor-group">
                    <Form.Field className="tna-question-bank__label-input">
                      <label>{t('IWS Skill')}</label>
                      <SkillsEditor
                        nullable
                        skillType={SkillType.IWS}
                        className="planit-user-dropdown-custom"
                        placeholder={t('Select IWS Skill')}
                        value={this.state.skill}
                        onChange={s => this.onChangeSkill(s)}
                        clearable
                      />
                    </Form.Field>
                  </Form.Group>

                  <Form.Group className="tna-question-bank__selctor-group">
                    {(areYouPoc || isAdminOrPwerInst) && this.changeQuestionBankStore.state.item.locationRequired && (
                      <Form.Field
                        key={'location-editor-field-edit-iws'}
                        className="tna-question-bank__label-input"
                        required
                        error={this.changeQuestionBankStore.state.result && !this.changeQuestionBankStore.state.item.locationId}
                      >
                        <label>{t('Location')}</label>
                        <LocationEditorWithRoleLocations
                          key={'location-editor-edit-iws'}
                          className="planit-user-dropdown-custom"
                          value={this.changeQuestionBankStore.state.item.locationId}
                          locationsReceived={areYouPoc ? pocLocations : []}
                          onChange={location =>
                            this.handleValue(
                              nameof<ChangeIwsQuestionBankDto>('locationId'),
                              location ? (location.id ? location.id : location) : null
                            )
                          }
                          skipDefaultClass
                          customPlaceholder={"PoC's Location"}
                        />
                      </Form.Field>
                    )}
                  </Form.Group>
                  <Form.Group className="tna-question-bank__selctor-textarea">
                    <Form.Field
                      required
                      className="tna-question-bank__label-textarea"
                      error={this.state.userClickedSave && this.changeQuestionBankStore.state.item.question.text.length === 0}
                    >
                      <label>{t('Question')}</label>
                      <TextArea
                        maxLength="500"
                        className="tna-question-bank__custom-editorequest__textarea"
                        value={
                          this.changeQuestionBankStore.state.item.question ? this.changeQuestionBankStore.state.item.question.text : ''
                        }
                        onChange={(e, { value }) => this.onChangeQuestion(value.toString())}
                      />

                      <Button className="custom-table-add-row-btn add-row-btn" type="button" onClick={this.addQuestionsTranslation}>
                        {i18n.t('Add Translation ')}
                      </Button>
                    </Form.Field>
                  </Form.Group>
                  {this.state.questionTranslations.length > 0 && <>{this.showQuestionTranslationRows()}</>}
                </div>
              </Form>
            )}
          </Container>
        </Modal.Content>
        <Modal.Actions className="new-tna-question-bank__btns">
          <Button onClick={this.onCancelNewItem} basic>
            {t('Cancel')}
          </Button>
          <Button onClick={this.onChangeItem} positive>
            {t('Save')}
          </Button>
        </Modal.Actions>
      </Modal>
    );
  }
}

// Wire up the React component to the Redux store
export default withTranslation()(ChangeIwsQuestionBankView);
